import React, { useState } from 'react';
import './Form.css';
import cx from 'classnames';
import phone from 'phone';
import { OTHER_DISEASE } from 'utils/constants/disease';
// eslint-disable-next-line import/no-cycle
import Checkbox from 'new-ui/Components/Form/Checkbox';
import Radio from 'new-ui/Components/Form/Radio';
import Range from 'new-ui/Components/Form/Range';
import Text from 'new-ui/Components/Form/Text';
// eslint-disable-next-line import/no-cycle
import Input from 'new-ui/Components/Form/Input';
import PhoneInput from 'new-ui/Components/Form/PhoneInput';
import Select from 'new-ui/Components/Form/Select';
import LocationInput from 'new-ui/Components/Form/LocationInput';
import PropTypes from 'prop-types';
import { getSearchParams } from 'functions/getSearchParams';
import { useBlurActiveElement } from 'utils/hooks/useBlurActiveElement';
import { COLORS } from 'new-ui/constants';
// eslint-disable-next-line import/no-cycle
import { getDiseaseOptions } from 'modules/questionnaire/helpers';
import SelectV2 from './Select.v2';
import DateInput from './Date';
import { PopupFooter } from '../Popup';
import Button from '../Button';
import IndicationChooser from './IndicationChooser';

export const FORM_INPUT_TYPES = {
  CHECKBOX: 'checkbox',
  MULTI_CHECKBOX: 'multi-checkbox',
  PASSWORD: 'password',
  PHONE: 'phone',
  RADIO: 'radio',
  RANGE: 'range',
  INPUT: 'input',
  TEXT: 'text',
  DATE: 'date',
  SELECT: 'select',
  SELECT_V2: 'select_v2',
  LOCATION: 'location',
  LIST: '',
  INDICATION_CHOOSER: 'indication_chooser',
  SEARCH_INDICATION_CHOOSER: 'search_indication_chooser',
};

export const FORM_THEMES = {
  DEFAULT: 'default',
  ORANGE: 'orange',
};

const getHelperTextClassName = ({ type = 'error' }) => (type === 'error' ? 'error-input-helper-text' : '');

const Form = (props) => {
  const { blurActiveElement } = useBlurActiveElement();
  const [renderKey, setRenderKey] = useState(1);
  const {
    onChange = () => {},
    submit,
    popupMode = false,
    form,
    className = '',
    keyup,
    onSubmit,
    onenter,
    append,
    id,
    theme = FORM_THEMES.DEFAULT,
  } = props;
  let { data, inputs } = props;
  const { inputHelperText } = props;

  if (form) {
    data = form.data;
    inputs = form.inputs;
    form.render = () => { setRenderKey(Math.random()); }; // for re-rendering the form after data change;
  } else if (!data && !inputs && !form) {
    return null;
  }

  const inputChange = (name, newValue) => {
    data[name] = newValue;
    onChange(data, name, newValue);
  };

  const getInput = (input, inputKey, tabindex) => {
    const value = data[input.name];
    switch (input.type) {
      // eslint-disable-next-line default-case-last
      default:
      case FORM_INPUT_TYPES.PASSWORD:
      case FORM_INPUT_TYPES.INPUT:
        return (
          <Input
            key={inputKey}
            theme={theme}
            input={input}
            title={input.title || input.name}
            value={value}
            required={input.required}
            isPassword={input.type === FORM_INPUT_TYPES.PASSWORD}
            icon={input.icon}
            email={input.email}
            placeholder={input.placeholder}
            keyup={keyup}
            onenter={onenter}
            className={input.className}
            tabindex={tabindex}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
      // is not used
      case FORM_INPUT_TYPES.INDICATION_CHOOSER:
        return (
          <IndicationChooser
            key={inputKey}
            {...input}
            value={value}
            onChange={(newValue) => inputChange(input.name, newValue)}
            getIndications={async () => (
              (await getDiseaseOptions()).filter(({ value: indicationValue }) => indicationValue !== OTHER_DISEASE)
            )}
          />
        );
      case FORM_INPUT_TYPES.SEARCH_INDICATION_CHOOSER:
        return (
          <IndicationChooser
            key={inputKey}
            {...input}
            value={value}
            onChange={(newValue) => inputChange(input.name, newValue)}
            getIndications={async () => {
              const { indicationLabelsData } = await getSearchParams();
              return indicationLabelsData.map(({ label, value: indicationValue, autoSelectType }) => ({
                label,
                type: autoSelectType,
                value: indicationValue,
              }));
            }}
          />
        );
      case FORM_INPUT_TYPES.DATE:
        return (
          <DateInput
            key={inputKey}
            theme={theme}
            tabindex={tabindex}
            input={input}
            value={value}
            {...input}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.PHONE:
        return (
          <PhoneInput
            key={inputKey}
            value={value}
            theme={theme}
            tabindex={tabindex}
            placeholder={input.placeholder}
            country={input.country}
            keyup={keyup}
            onenter={onenter}
            onChange={(rawNextValue) => {
              let nextValue = rawNextValue;
              if (rawNextValue && !rawNextValue.startsWith('+')) {
                nextValue = `+${rawNextValue}`;
              }

              inputChange(input.name, nextValue);
            }}
          />
        );
      case FORM_INPUT_TYPES.TEXT:
        return (
          <Text
            key={inputKey}
            theme={theme}
            tabindex={tabindex}
            title={input.title || input.name}
            value={data[input.name]}
            height={input.height}
            required={input.required}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.LIST:
        return (
          <Checkbox
            isList
            key={inputKey}
            theme={theme}
            tabindex={tabindex}
            title={input.title || input.name}
            checked={data[input.name] || false}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.CHECKBOX:
        return (
          <Checkbox
            key={inputKey}
            theme={theme}
            tabindex={tabindex}
            title={input.title || input.name}
            checked={data[input.name] || false}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.MULTI_CHECKBOX:
        // eslint-disable-next-line no-case-declarations
        const jsx = [];
        input.options.forEach((checkbox, key) => {
          const name = typeof checkbox === 'string' ? checkbox : checkbox.name;
          const values = data[input.name] || [];
          const _checkbox = (
            <Checkbox
              key={key + inputKey}
              tabindex={tabindex}
              theme={theme}
              title={typeof checkbox === 'string' ? checkbox : (checkbox.title || checkbox.name)}
              checked={values.indexOf(name) !== -1}
              onChange={
                (newValue) => {
                  if (values.indexOf(name) !== -1) { values.splice(values.indexOf(name), 1); }
                  if (newValue === true) { values.push(name); }
                  onChange(data, undefined, name);
                }
              }
            />
          );

          jsx.push(
            _checkbox,
          );
        });
        return jsx;
      case FORM_INPUT_TYPES.RADIO:
        return (
          <Radio
            key={inputKey}
            theme={theme}
            options={input.options}
            value={data[input.name]}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.RANGE:
        return (
          <Range
            key={inputKey}
            theme={theme}
            tabindex={tabindex}
            title={input.title}
            single={input.single}
            options={input.options}
            value={data[input.name]}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.SELECT:
        return (
          <Select
            key={inputKey}
            theme={theme}
            {...input}
            tabIndex={tabindex}
            value={data[input.name]}
            onEnter={onenter}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
                if (input.onchange) input.onchange(newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.SELECT_V2:
        return (
          <SelectV2
            key={inputKey}
            theme={theme}
            {...input}
            value={data[input.name]}
            tabindex={tabindex}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
                if (input.onchange) input.onchange(newValue);
              }
            }
          />
        );
      case FORM_INPUT_TYPES.LOCATION:
        return (
          <LocationInput
            key={inputKey}
            theme={theme}
            tabindex={tabindex}
            {...input}
            value={data[input.name]}
            onChange={
              (newValue) => {
                inputChange(input.name, newValue);
              }
            }
          />
        );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    blurActiveElement();

    if (submit) {
      submit.action();
    } else if (onSubmit) {
      onSubmit();
    }
  };

  const render = () => Object.keys(inputs).map((inputName, key) => {
    const input = inputs[inputName];
    const helperText = inputHelperText?.[inputName];
    // eslint-disable-next-line react/prop-types
    input.name = inputName;
    return (
      <div className={`form-part ${inputName} ${input.hidden ? 'form-input-hidden' : ''} ${input.disabled ? 'form-part-disabled' : ''}`} key={key}>
        {((input.label || input.title) && !input.noHeader) ? (
          <div className="form-input-title">
            {input.label || input.title}
            {' '}
            {input.required ? <span className="required">*</span> : null}
          </div>
        ) : null}
        <div className={`form-input ${input.type} ${input.className || ''}`}>{getInput(input, renderKey, 0)}</div>
        {helperText && (
          <p className={getHelperTextClassName(helperText)}>
            {helperText.value}
          </p>
        )}
      </div>
    );
  });
  return (
    <form id={id} className={cx('form', className)} onSubmit={handleSubmit}>
      {render()}
      {append || null}
      { submit
        ? (popupMode ? (
          <PopupFooter>
            <input type="submit" style={{ backgroundColor: COLORS.primaryButton }} className="button form-submit" value={submit.title} />
          </PopupFooter>
        ) : <Button {...submit} />) : null}
    </form>
  );
};

Form.propTypes = {
  inputs: PropTypes.objectOf(PropTypes.shape({
    type: PropTypes.oneOf(Object.values(FORM_INPUT_TYPES)).isRequired,
    required: PropTypes.bool,
    noHeader: PropTypes.bool,
    icon: PropTypes.string,
    title: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    hidden: PropTypes.bool,
  })),
  onChange: PropTypes.func,
  submit: PropTypes.object,
  popupMode: PropTypes.bool,
  data: PropTypes.object,
  form: PropTypes.object,
  className: PropTypes.string,
  theme: PropTypes.string,
  keyup: PropTypes.func,
  onSubmit: PropTypes.func,
  onenter: PropTypes.func,
  append: PropTypes.any,
  api: PropTypes.any,
  inputHelperText: PropTypes.objectOf(PropTypes.shape({
    type: PropTypes.oneOf(['error']),
    value: PropTypes.string.isRequired,
  })),
  id: PropTypes.string,
};

export default Form;

const validateEmail = (email) => String(email)
  .toLowerCase()
  .match(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  );

export const FORM_VALIDATORS = {
  EMAIL: (email) => validateEmail(email),
  PHONE: (phoneNumber) => phone(phoneNumber?.startsWith('+') ? phoneNumber : `+${phoneNumber}`).isValid,
};
